import {
  HIRED,
  IN_PROCESS,
  INTRODUCED,
  OFFERED,
  ON_HOLD,
  REJECTED,
  SOURCED,
} from '@/constants/statuses';
import { tCatalog } from '@/i18n/tCatalog';
import { getRandomColor } from '@/theme/avatarPalette';

import type { Candidate, CandidateInfo } from '@/types/candidate';
import type { Interview, InterviewRoom } from '@/types/interview';
import type {
  Certificate,
  City,
  Role,
  Skill,
  Specialization,
  Education,
} from '@/types/reference';

type StageGroupedCandidate = Candidate & {
  stage_name: string;
  color: { main: string; light: string };
  interview_id: string;
};

/**
 * Returns true when the candidate profile has a stored CV file URL.
 * Additional documents are not a CV. Empty, whitespace, and null are missing.
 */
export const candidateHasAttachedCv = (
  cv: string | null | undefined,
): boolean => Boolean(cv?.trim());

/**
 * Returns true when a candidate has an interview for the given job that should
 * block re-submission.
 */
export const hasBlockingInterviewForJob = (
  interviews: InterviewRoom[] | undefined,
  jobId: string | undefined,
): boolean => {
  if (!jobId || !interviews?.length) return false;

  return (
    interviews.some((int) => int.job.id === jobId && !int.deleted_in_ats) ||
    false
  );
};

export const groupByStageName = (
  stages: Interview[],
  list: Record<string, StageGroupedCandidate[]>,
) => {
  return stages.reduce((group, stage) => {
    if (stage.status === OFFERED) {
      (group[OFFERED] = group[OFFERED] || []).push({
        ...stage.candidate,
        stage_name: OFFERED,
        color: getRandomColor(),
        interview_id: stage.id,
      });
    } else if (stage.status === REJECTED) {
      /* empty */
    } else if (stage.status === HIRED) {
      (group[HIRED] = group[HIRED] || []).push({
        ...stage.candidate,
        stage_name: HIRED,
        color: getRandomColor(),
        interview_id: stage.id,
      });
    } else if ([SOURCED, INTRODUCED].includes(stage.status)) {
      /* empty */
    } else {
      const key = stage?.current_stage?.stage_name || null;
      if (key?.toLowerCase() === '1st interview') {
        (group['1st Interview'] = group['1st Interview'] || []).push({
          ...stage.candidate,
          stage_name: key ?? '',
          color: getRandomColor(),
          interview_id: stage.id,
        });
      } else {
        (group['2nd+ Interview'] = group['2nd+ Interview'] || []).push({
          ...stage.candidate,
          color: getRandomColor(),
          stage_name: key ?? '',
          interview_id: stage.id,
        });
      }
    }

    return group;
  }, list);
};

export const getCandidateStatistics = (stages: Interview[]) => {
  return stages.reduce(
    (group, stage) => {
      const key = stage?.status || null;
      if (key && key in group) {
        group[key as keyof typeof group] = group[key as keyof typeof group] + 1;
      }
      group.sum += 1;

      return group;
    },
    { sum: 0, [INTRODUCED]: 0, [IN_PROCESS]: 0, [HIRED]: 0, [REJECTED]: 0 },
  );
};

export const getNameFirstLetters = (name: string) => {
  const tokens = name?.split(/\s+/).filter((token) => /^\p{L}/u.test(token));
  const first = tokens?.[0]?.[0];
  if (!first) return 'A';
  const second = tokens[1]?.[0];
  return (second ? first + second : first).toUpperCase();
};

export const filterSkills = (skills: Skill[], ids: string[]) => {
  return ids.map((id) => skills.find((skill) => skill.id === id)?.name);
};

export const filterRoles = (roles: Role[], ids: string[] | string) => {
  if (Array.isArray(ids)) {
    return ids.map((id) => roles.find((role) => role.id === id)?.name);
  } else {
    return roles.find((role) => role.id === ids)?.name;
  }
};

export const filterCities = (cities: City[], id: string) => {
  return cities.find((city) => city.id === id)?.name;
};

export const filterDomains = (
  domains: Specialization[],
  ids: string[],
): (string | undefined)[] => {
  return ids.map((id) => {
    const domain = domains.find((domain) => domain.id === id);
    return domain ? domain.name : undefined;
  });
};

/** Specializations may be API objects or name strings from `getAllInfo` enrichment. */
type CandidateCatalogLabelSource = string | { name: string } | null | undefined;

function catalogLabels(
  items: CandidateCatalogLabelSource[] | undefined,
  section: 'skills' | 'specializations',
): string[] {
  return (items ?? [])
    .map((item) => {
      if (item == null) return '';
      if (typeof item === 'object') {
        return item.name ? tCatalog(section, item.name) : '';
      }
      return item ? tCatalog(section, item) : '';
    })
    .filter((label) => label.length > 0);
}

export function formatCandidateSpecializationLabels(
  specializations: CandidateCatalogLabelSource[] | undefined,
): string {
  return catalogLabels(specializations, 'specializations').join(', ');
}

/** Skills may be API objects or name strings from `getAllInfo` enrichment. */
export function formatCandidateSkillLabels(
  skills: CandidateCatalogLabelSource[] | undefined,
): string {
  return catalogLabels(skills, 'skills')
    .sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    )
    .join(', ');
}

export const filterCertificates = (
  certificates: Certificate[],
  ids: string[],
) => {
  return ids.map(
    (id) => certificates.find((certificate) => certificate.id === id)?.name,
  );
};

export const filterEducation = (education: Education[], ids: string[]) =>
  ids.map((id) => education.find((ed) => ed.id === id)?.name);

export const toRelationIds = (
  items: Array<string | { id: string }> | undefined,
) => (items ?? []).map((item) => (typeof item === 'string' ? item : item.id));

type IgetAllInfo = {
  candidates: CandidateInfo[];
  cities: City[];
  skills: Skill[];
  roles: Role[];
  domains: Specialization[];
  certificates: Certificate[];
  education: Education[];
  seniorities?: { id: string; name: string }[];
};

// @TODO do we really need roles?
export const getAllInfo = ({
  candidates,
  cities,
  skills,
  domains,
  certificates,
  education,
  seniorities,
}: IgetAllInfo) => {
  return candidates.map((candidate) => {
    // Handle city_of_residence - it can be either a city ID or already a city name string
    let cityName: string | undefined;
    if (typeof candidate.city_of_residence === 'string') {
      // Check if it's already a city name (not an ID)
      const cityById = cities.find(
        (city) => city.id === candidate.city_of_residence,
      );
      if (cityById) {
        // It's a city ID, convert to name
        cityName = cityById.name;
      } else {
        // It's already a city name
        cityName = candidate.city_of_residence;
      }
    }

    const seniorityId =
      typeof candidate.seniority === 'object' && candidate.seniority
        ? candidate.seniority.id
        : candidate.seniority;
    const seniorityName =
      typeof candidate.seniority === 'object' && candidate.seniority?.name
        ? candidate.seniority.name
        : seniorities?.find((sen) => sen.id === seniorityId)?.name;

    return {
      ...candidate,
      skills: filterSkills(skills, toRelationIds(candidate.skills)) as [],
      city_of_residence: cityName || '',
      specializations: filterDomains(
        domains,
        toRelationIds(candidate.specializations),
      ) as [],
      certificates: filterCertificates(
        certificates,
        toRelationIds(candidate.certificates),
      ) as [],
      education: filterEducation(
        education,
        toRelationIds(candidate.education),
      ) as [],
      seniority: seniorityName
        ? { id: seniorityId, name: seniorityName }
        : candidate.seniority,
      color: candidate.color ?? getRandomColor(),
    };
  });
};

export const groupByStages = (
  candidates: CandidateInfo[],
  candidateStageGroup: Record<string, CandidateInfo[]>,
  sourcedStageEnabled: boolean,
  showReject: boolean,
  showHired: boolean,
) => {
  // Seeds reduce and defines kanban column order. Job/company/date filtering is
  // server-side via shared listing filters — not applied here.
  const stages: Record<string, CandidateInfo[]> = {
    ...(sourcedStageEnabled ? { [SOURCED]: [] } : {}),
    [ON_HOLD]: [],
    [INTRODUCED]: [],
    ...candidateStageGroup,
    [OFFERED]: [],
    ...(showReject ? { [REJECTED]: [] } : {}),
    [HIRED]: [],
  };
  return candidates.reduce((totalAcc, candidate) => {
    if (!candidate?.interviews?.length) return totalAcc;
    return candidate.interviews.reduce((candidateAcc, interview) => {
      if (!interview?.job) return candidateAcc;
      const isBuiltinStage = [
        sourcedStageEnabled && SOURCED,
        ON_HOLD,
        INTRODUCED,
        OFFERED,
        showReject && REJECTED,
        showHired && HIRED,
      ].includes(interview.status);
      const isValidInterviewStage =
        interview.current_stage &&
        interview.status !== REJECTED &&
        interview.status !== HIRED;
      if (!isBuiltinStage && !isValidInterviewStage) return candidateAcc;

      const stageName = interview.current_stage?.stage_name;
      if (!isBuiltinStage && !stageName) return candidateAcc;

      const key = isBuiltinStage ? interview.status : stageName!.toLowerCase();
      return {
        ...candidateAcc,
        [key]: [
          ...candidateAcc[key],
          { ...candidate, interviews: [interview] },
        ],
      };
    }, totalAcc);
  }, stages);
};

export const orderTasks = <T extends { is_planning_task: boolean }>(
  array: T[],
) => {
  const newArray: T[] = [];
  array.forEach((e) => {
    if (e.is_planning_task) newArray.unshift(e);
    else newArray.push(e);
  });

  return newArray;
};

/**
 * Formats a number with locale-specific thousand separators and decimal points.
 * @param value - The numeric value to format
 * @param locale - The locale to use (default: 'nl-NL' for European format)
 * @param decimals - Maximum number of decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | string | null | undefined,
  locale: string = 'nl-NL',
  decimals: number = 2,
): string => {
  if (value === null || value === undefined || value === '') return '0';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '0';

  //double check if locale is valid
  try {
    Intl.NumberFormat.supportedLocalesOf([locale]);
  } catch {
    locale = 'nl-NL';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(numValue);
};
